import {
  createServer,
  type IncomingMessage,
  type ServerResponse,
} from 'node:http';
import { readFileSync, existsSync, readdirSync } from 'node:fs';
import { join, extname } from 'node:path';
import { PassThrough, Readable } from 'node:stream';
import { createRequire } from 'node:module';
import { Hono } from 'hono';
import archiver from 'archiver';
import {
  listSessions,
  deleteSession,
  getSession,
  type SessionSummary,
} from '../recording/storage.js';
import { AppError, notFound, badRequest, ErrorCode } from '../errors.js';
import { alignActions, type AlignableAction } from '../compare/alignment.js';
import { metrics, type MetricSnapshot } from '../metrics.js';
import { planContext } from '../billing/context.js';
import { Feature, Plan, getPlanLimits } from '../billing/entitlements.js';
import { LicenseService } from '../licensing/service.js';
import type { SyncQueue } from '../sync/queue.js';

function loadPkgVersion(): string {
  const _require = createRequire(import.meta.url);
  // Try both bundled (dist/cli.js → ../package.json) and dev (src/web/server.ts → ../../package.json)
  for (const rel of ['../package.json', '../../package.json']) {
    try {
      return (_require(rel) as { version: string }).version;
    } catch { /* try next */ }
  }
  return '0.0.0';
}
const pkg = { version: loadPkgVersion() };

const MIME_TYPES: Record<string, string> = {
  '.html': 'text/html; charset=utf-8',
  '.js': 'application/javascript',
  '.css': 'text/css',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.svg': 'image/svg+xml',
  '.ico': 'image/x-icon',
  '.json': 'application/json',
  '.woff': 'font/woff',
  '.woff2': 'font/woff2',
};

/** Build and pipe a session ZIP archive to a writable stream. */
function pipeSessionZip(storageDir: string, id: string, dest: import('node:stream').Writable): void {
  const sessionDir = join(storageDir, id);
  const archive = archiver('zip', { zlib: { level: 6 } });

  archive.pipe(dest);
  archive.on('error', (err) => dest.destroy(err));

  archive.file(join(sessionDir, 'session.json'), { name: 'session.json' });

  const framesDir = join(sessionDir, 'frames');
  if (existsSync(framesDir)) {
    for (const file of readdirSync(framesDir)) {
      if (file.endsWith('.png')) {
        archive.file(join(framesDir, file), { name: `frames/${file}` });
      }
    }
  }

  const readme = [
    'BrowserForge Session Export',
    '===========================',
    '',
    'Files:',
    '  session.json     Session manifest with action timeline',
    '  frames/*.png     Screenshot frames (one per action)',
    '',
    `Session ID: ${id}`,
    `Exported:   ${new Date().toISOString()}`,
  ].join('\n');
  archive.append(readme, { name: 'README.txt' });

  archive.finalize().catch(() => {/* handled via error event */});
}

/** Shape returned by GET /api/sessions */
export interface ApiSessionSummary {
  id: string;
  startedAt: string;
  endedAt: string | null;
  duration: number;
  actionCount: number;
  firstUrl: string | null;
  thumbnailPath: string | null;
}

function summaryToApi(s: SessionSummary): ApiSessionSummary {
  return {
    id: s.id,
    startedAt: s.startedAt,
    endedAt: s.endedAt,
    duration: s.duration,
    actionCount: s.actionCount,
    firstUrl: s.firstUrl,
    thumbnailPath: s.hasThumbnail ? `/api/sessions/${s.id}/thumbnail` : null,
  };
}

export interface CreateAppOptions {
  storageDir: string;
  licenseService?: LicenseService;
  syncQueue?: SyncQueue;
}

/** Create the Hono app with API routes. Exported for testing without a live HTTP server. */
export function createApp(storageDir: string, licenseService?: LicenseService, syncQueue?: SyncQueue): Hono {
  const svc = licenseService ?? new LicenseService();
  const app = new Hono();

  // Global error handler
  app.onError((err, c) => {
    if (err instanceof AppError) {
      return c.json(
        { error: { code: err.code, message: err.message } },
        err.statusCode as Parameters<typeof c.json>[1],
      );
    }
    return c.json(
      { error: { code: 'INTERNAL', message: 'Internal server error' } },
      500,
    );
  });

  // Health check
  app.get('/api/health', (c) => {
    const { total } = listSessions(storageDir);
    return c.json({
      status: 'ok',
      version: pkg.version,
      uptime: process.uptime(),
      sessions: total,
    });
  });

  // Operational metrics
  app.get('/api/metrics', (c) => {
    const snap: MetricSnapshot = metrics.snapshot();
    const format = c.req.query('format');

    if (format === 'prometheus') {
      const lines: string[] = [
        `# HELP sessions_recorded_total Total sessions recorded`,
        `# TYPE sessions_recorded_total counter`,
        `sessions_recorded_total ${snap.sessionsRecorded}`,
        `# HELP sessions_active Current active recording sessions`,
        `# TYPE sessions_active gauge`,
        `sessions_active ${snap.sessionsActive}`,
        `# HELP actions_recorded_total Total actions recorded`,
        `# TYPE actions_recorded_total counter`,
        `actions_recorded_total ${snap.actionsRecorded}`,
        `# HELP screenshots_captured_total Total screenshots captured`,
        `# TYPE screenshots_captured_total counter`,
        `screenshots_captured_total ${snap.screenshotsCaptured}`,
        `# HELP screenshots_failed_total Total screenshot failures`,
        `# TYPE screenshots_failed_total counter`,
        `screenshots_failed_total ${snap.screenshotsFailed}`,
        `# HELP ws_connections_active Current active WebSocket connections`,
        `# TYPE ws_connections_active gauge`,
        `ws_connections_active ${snap.wsConnectionsActive}`,
        `# HELP ws_connections_total Total WebSocket connections ever made`,
        `# TYPE ws_connections_total counter`,
        `ws_connections_total ${snap.wsConnectionsTotal}`,
        `# HELP proxy_messages_forwarded_total Total proxy messages forwarded`,
        `# TYPE proxy_messages_forwarded_total counter`,
        `proxy_messages_forwarded_total ${snap.proxyMessagesForwarded}`,
        `# HELP uptime_seconds Process uptime in seconds`,
        `# TYPE uptime_seconds gauge`,
        `uptime_seconds ${snap.uptimeSeconds}`,
        '',
      ];
      return new Response(lines.join('\n'), {
        headers: { 'Content-Type': 'text/plain; version=0.0.4; charset=utf-8' },
      });
    }

    return c.json(snap);
  });

  // Billing status
  app.get('/api/billing/status', (c) => {
    const plan = planContext.getPlan();
    const limits = getPlanLimits(plan);
    const features = Object.values(Feature).filter((f) =>
      planContext.hasFeature(f),
    );
    return c.json({ plan, features, limits });
  });

  // License status
  app.get('/api/license/status', (c) => {
    const status = svc.getStatus();
    return c.json(status);
  });

  // Activate a license key
  app.post('/api/license/activate', async (c) => {
    let body: { key?: unknown };
    try {
      body = await c.req.json() as { key?: unknown };
    } catch {
      throw badRequest('Request body must be JSON with a "key" field');
    }
    if (typeof body.key !== 'string' || !body.key.trim()) {
      throw badRequest('"key" must be a non-empty string');
    }
    const result = await svc.activate(body.key.trim());
    if (!result.success) {
      return c.json({ success: false, error: result.error }, 400);
    }
    // Sync planContext so the running server reflects the new plan
    if (result.plan) planContext.setPlan(result.plan);
    return c.json({ success: true, plan: result.plan });
  });

  // Deactivate the current license
  app.post('/api/license/deactivate', (c) => {
    svc.deactivate();
    planContext.setPlan(Plan.FREE);
    return c.json({ success: true });
  });

  // Get upgrade checkout URL for a given plan
  app.get('/api/billing/upgrade', (c) => {
    const plan = c.req.query('plan');
    const validPlans = ['pro', 'team'];
    if (!plan || !validPlans.includes(plan.toLowerCase())) {
      throw badRequest(`"plan" query param must be one of: ${validPlans.join(', ')}`);
    }
    const cloudBase = process.env['BROWSERFORGE_CLOUD_URL'] ?? 'https://buy.browserforge.dev';
    // Validate the base URL is HTTPS to prevent XSS via env var injection
    let parsed: URL;
    try {
      parsed = new URL(cloudBase);
    } catch {
      throw badRequest('Invalid BROWSERFORGE_CLOUD_URL configuration');
    }
    if (parsed.protocol !== 'https:') {
      throw badRequest('BROWSERFORGE_CLOUD_URL must use HTTPS');
    }
    const url = `${parsed.origin}/checkout/${plan.toLowerCase()}`;
    return c.json({ url });
  });

  // List sessions with optional pagination and search
  app.get('/api/sessions', (c) => {
    const limitParam = c.req.query('limit');
    const cursor = c.req.query('cursor') || undefined;
    const query = c.req.query('q') || undefined;

    const DEFAULT_LIMIT = 50;
    const MAX_LIMIT = 200;
    const limit = limitParam
      ? Math.min(Math.max(1, parseInt(limitParam, 10) || DEFAULT_LIMIT), MAX_LIMIT)
      : DEFAULT_LIMIT;

    const { sessions, nextCursor, total } = listSessions(storageDir, { limit, cursor, query });
    return c.json({
      sessions: sessions.map(summaryToApi),
      nextCursor,
      total,
    });
  });

  // Compare two sessions — must be registered before :id routes
  app.get('/api/sessions/compare', (c) => {
    const idA = c.req.query('a');
    const idB = c.req.query('b');
    if (!idA || !idB) {
      throw badRequest('Both query params "a" and "b" are required');
    }

    const sessionA = getSession(storageDir, idA);
    if (!sessionA) throw notFound('session', idA);

    const sessionB = getSession(storageDir, idB);
    if (!sessionB) throw notFound('session', idB);

    const actionsA: AlignableAction[] = sessionA.actions.map((a) => ({
      seq: a.seq,
      tool: a.tool,
      args: a.args,
    }));
    const actionsB: AlignableAction[] = sessionB.actions.map((a) => ({
      seq: a.seq,
      tool: a.tool,
      args: a.args,
    }));

    const alignment = alignActions(actionsA, actionsB);
    return c.json({ sessionA, sessionB, alignment });
  });

  // Delete a session
  app.delete('/api/sessions/:id', (c) => {
    const { id } = c.req.param();
    const manifest = getSession(storageDir, id);
    if (!manifest) {
      throw notFound('session', id);
    }
    deleteSession(storageDir, id);
    return c.json({ ok: true });
  });

  // Get single session manifest (full detail with actions)
  app.get('/api/sessions/:id', (c) => {
    const { id } = c.req.param();
    const manifest = getSession(storageDir, id);
    if (!manifest) {
      throw notFound('session', id);
    }
    return c.json(manifest);
  });

  // Serve session frame PNG
  app.get('/api/sessions/:id/frames/:filename', (c) => {
    const { id, filename } = c.req.param();

    // Security: only allow .png files, no path traversal
    if (!filename.endsWith('.png') || filename.includes('..') || filename.includes('/')) {
      return c.json({ error: 'Invalid filename' }, 400);
    }

    const framePath = join(storageDir, id, 'frames', filename);
    if (!existsSync(framePath)) {
      return c.json({ error: 'Not found' }, 404);
    }
    const data = readFileSync(framePath);
    return new Response(data, {
      headers: {
        'Content-Type': 'image/png',
        'Cache-Control': 'public, max-age=31536000, immutable',
      },
    });
  });

  // Export session as ZIP download (streams via web ReadableStream)
  app.get('/api/sessions/:id/export', (c) => {
    const { id } = c.req.param();
    if (!getSession(storageDir, id)) {
      throw notFound('session', id);
    }

    const pt = new PassThrough();
    pipeSessionZip(storageDir, id, pt);

    // Convert Node stream → web ReadableStream for Fetch-based Hono response
    const webStream = Readable.toWeb(pt) as ReadableStream;

    return new Response(webStream, {
      headers: {
        'Content-Type': 'application/zip',
        'Content-Disposition': `attachment; filename="browserforge-${id}.zip"`,
      },
    });
  });

  // Serve session thumbnail
  app.get('/api/sessions/:id/thumbnail', (c) => {
    const { id } = c.req.param();
    const thumbPath = join(storageDir, id, 'thumbnail.png');
    if (!existsSync(thumbPath)) {
      return c.json({ error: 'Not found' }, 404);
    }
    const data = readFileSync(thumbPath);
    return new Response(data, {
      headers: { 'Content-Type': 'image/png' },
    });
  });

  // ─── Cloud sync routes (Pro/Team feature) ────────────────────────
  //
  // POST /api/cloud/sync/:sessionId — manually trigger sync for a session
  app.post('/api/cloud/sync/:sessionId', (c) => {
    if (!planContext.hasFeature(Feature.CLOUD_SYNC)) {
      return c.json({
        error: { code: 'UPGRADE_REQUIRED', message: 'Cloud sync requires a Pro or Team plan' },
      }, 403);
    }

    if (!syncQueue) {
      return c.json({
        error: { code: 'SYNC_UNAVAILABLE', message: 'Sync queue not initialized' },
      }, 503);
    }

    const { sessionId } = c.req.param();
    const manifest = getSession(storageDir, sessionId);
    if (!manifest) {
      throw notFound('session', sessionId);
    }

    syncQueue.enqueue(sessionId);
    return c.json({ ok: true, sessionId, status: 'enqueued' });
  });

  // GET /api/cloud/sync/status — overall sync queue status
  app.get('/api/cloud/sync/status', (c) => {
    if (!planContext.hasFeature(Feature.CLOUD_SYNC)) {
      return c.json({
        error: { code: 'UPGRADE_REQUIRED', message: 'Cloud sync requires a Pro or Team plan' },
      }, 403);
    }

    if (!syncQueue) {
      return c.json({
        error: { code: 'SYNC_UNAVAILABLE', message: 'Sync queue not initialized' },
      }, 503);
    }

    return c.json(syncQueue.getStatus());
  });

  return app;
}

export interface WebServerOptions {
  port: number;
  host: string;
  storageDir: string;
  /** Directory containing the built UI (dist/ui). */
  staticDir: string;
  /** Optional LicenseService instance; defaults to a new service with ~/.browserforge. */
  licenseService?: LicenseService;
  /** Optional SyncQueue instance for cloud sync. */
  syncQueue?: SyncQueue;
}

/** Bridge Node.js IncomingMessage into a Fetch-compatible Request for Hono. */
async function nodeReqToFetch(
  req: IncomingMessage,
  host: string,
): Promise<Request> {
  const url = `http://${host}${req.url ?? '/'}`;
  const body = await new Promise<Buffer>((resolve) => {
    const chunks: Buffer[] = [];
    req.on('data', (chunk: Buffer) => chunks.push(chunk));
    req.on('end', () => resolve(Buffer.concat(chunks)));
  });
  const headers: Record<string, string> = {};
  for (const [key, value] of Object.entries(req.headers)) {
    if (value !== undefined) {
      headers[key] = Array.isArray(value) ? value.join(', ') : value;
    }
  }
  return new Request(url, {
    method: req.method ?? 'GET',
    headers,
    body: body.length > 0 ? new Uint8Array(body) : undefined,
  });
}

/** Create the HTTP server (Hono API + static files). */
export function createWebServer(options: WebServerOptions) {
  const { port, host, storageDir, staticDir, licenseService, syncQueue } = options;
  const app = createApp(storageDir, licenseService, syncQueue);

  const handler = async (req: IncomingMessage, res: ServerResponse) => {
    const url = req.url ?? '/';

    // Export endpoint — stream ZIP directly to avoid buffering large archives
    const exportMatch = /^\/api\/sessions\/([^/]+)\/export$/.exec(url);
    if (exportMatch && req.method === 'GET') {
      const id = exportMatch[1];
      if (!getSession(storageDir, id!)) {
        res.statusCode = 404;
        res.setHeader('Content-Type', 'application/json');
        res.end(JSON.stringify({
          error: { code: ErrorCode.SESSION_NOT_FOUND, message: `session '${id}' not found` },
        }));
        return;
      }
      res.statusCode = 200;
      res.setHeader('Content-Type', 'application/zip');
      res.setHeader('Content-Disposition', `attachment; filename="browserforge-${id}.zip"`);
      pipeSessionZip(storageDir, id!, res);
      return;
    }

    // API routes → Hono
    if (url.startsWith('/api/')) {
      const fetchReq = await nodeReqToFetch(req, host);
      const honoRes = await app.fetch(fetchReq);
      res.statusCode = honoRes.status;
      honoRes.headers.forEach((value, key) => {
        res.setHeader(key, value);
      });
      const resBody = await honoRes.arrayBuffer();
      res.end(Buffer.from(resBody));
      return;
    }

    // Static assets
    const assetPath = url === '/' ? '/index.html' : url;
    const filePath = join(staticDir, assetPath);

    if (existsSync(filePath)) {
      const ext = extname(filePath);
      const mime = MIME_TYPES[ext] ?? 'application/octet-stream';
      res.setHeader('Content-Type', mime);
      res.statusCode = 200;
      res.end(readFileSync(filePath));
      return;
    }

    // SPA fallback: serve index.html for unknown routes
    const indexPath = join(staticDir, 'index.html');
    if (existsSync(indexPath)) {
      res.setHeader('Content-Type', 'text/html; charset=utf-8');
      res.statusCode = 200;
      res.end(readFileSync(indexPath));
      return;
    }

    res.statusCode = 404;
    res.end('Not found');
  };

  const server = createServer(handler);

  return {
    listen(): Promise<void> {
      return new Promise((resolve) => server.listen(port, host, resolve));
    },
    close(): Promise<void> {
      return new Promise((resolve, reject) =>
        server.close((err) => (err ? reject(err) : resolve())),
      );
    },
    server,
  };
}
